import gql from 'graphql-tag';
import fetch from 'isomorphic-fetch';

import { showConfirmationModal } from 'actions/confirmationModal';
import {
  endThumbnailFlow,
  finishThumbnailSave,
  toggleThumbnailUploading,
} from 'actions/editor';
import { showErrorBar } from 'actions/errorBar';
import {
  resetTranscriptState,
  setAnonCreatorMode,
  updateShareVideo,
  updateShareVideoAliases,
  updateShareVideoDownloadUrl,
  updateTranscriptDetails,
  updateTranscriptStatus,
  updateUserHasOpenedTranscript,
  updateVideoIframeVisibility,
  updateWaveformGenerationDetails,
  updateWaveformGenerationStatus,
  videoMoveSuccess,
} from 'actions/shareVideo';
import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerLeaveFullScreen,
  requestVideoPlayerPause,
  toggleCaptionsVisible,
  toggleTranscriptVisible,
  updateCaptionsDetails,
  updateFillerWords,
  updateTheaterMode,
  updateTranscriptStatusOnVideoPlayer,
  updateTrimmingFillerWords,
  updateVideoPlayerPosterUrl,
  updateVideoPlayerVideo,
} from 'actions/videoPlayer';
import {
  createNewFolderFailed,
  createNewFolderLoad,
  createNewFolderSuccess,
  updateShowMoveModal,
} from 'actions/videosDashboard';
import { isDev } from 'constants/environment';
import { ERROR } from 'constants/error-severities';
import {
  SHARE_VIDEO_NAME_UPDATED,
  TOGGLE_CAPTIONS_OFF,
  TOGGLE_CAPTIONS_ON,
  TRANSCRIPTION_ACCESSED,
  TRANSCRIPTION_CLOSE_OUT,
  UPDATE_TRANSCRIPT_BY_FILLER_WORD_REMOVAL,
  VIDEO_FAVORITED,
  VIDEO_MOVED,
} from 'constants/events';
import {
  EXP_PERSISTENT_RECORD_BUTTON_SIGNED_OUT,
  STORE_FILLER_WORD_RANGES,
} from 'constants/featureFlag';
import { HEADER_CONTENT_TYPE, STATUS_NOT_FOUND } from 'constants/http';
import { SHARE_VIDEO_FLIP_CAMERA } from 'constants/messages';
import { JSON as JSON_MIME } from 'constants/mimes';
import {
  LOOM_URI,
  MY_VIDEOS_PAGE,
  NOT_FOUND,
  SERVER_ERROR_PAGE,
} from 'constants/routes';
import { FLIPPED_CAMERA } from 'constants/videoSettings';
import {
  initVideoCreator as initVideoPlayerVideoCreator,
  updateVideoCreator as updateVideoPlayerCreator,
  updateVideoPlayerSourceCreator,
} from 'creators/videoPlayer';
import { updateTranscriptPhrases } from 'reducers/transcription';
import { selectPlayerTime } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { getFlagValueForUser } from 'utilities/featureFlag.js';
import { getGraphQLClient } from 'utilities/graphql';
import { createImage as preloadImage } from 'utilities/image';
import * as loggerx from 'utilities/loggerx';
import * as sentry from 'utilities/sentry';
import { getParam } from 'utilities/url';
import * as videoUtils from 'utilities/video';

import * as TRANSCRIPTION from '../../../shared/constants/videoTranscript';
import AddFavorite from './AddFavorite.graphql';
import CreateShareVideoAlias from './CreateShareVideoAlias.graphql';
import DeleteFavorite from './DeleteFavorite.graphql';
import DeleteShareVideoAlias from './DeleteShareVideoAlias.graphql';
import DestroyVideo from './DestroyVideo.graphql';
import FetchVideoTranscript from './FetchVideoTranscript.graphql';
import FetchVideoWaveform from './FetchVideoWaveform.graphql';
import GetBulkShareVideoAlias from './GetBulkShareVideoAlias.graphql';
import GetVideoThumbnails from './GetVideoThumbnails.graphql';
import HasAnonymousCreatorPrivileges from './HasAnonymousCreatorPrivileges.graphql';
import onGifGeneratedForVideo from './onGifGeneratedForVideo.graphql';
import UpdateFillerWordRemoval from './UpdateFillerWordRemoval.graphql';
import UpdateGifSettings from './UpdateGifSettings.graphql';
import UpdateVideoName from './UpdateVideoName.graphql';
import { getFeatureFlagCreator } from './featureFlag';
import { completeTriggerCreator } from './triggers.js';
import { markEligibleElementAsCompleteForSelectedWorkspace } from '../components/videos-dashboard-ta/dashboard-onboarding/utilities';
import {
  setNewCcExperienceData,
  setNewTranscriptExperienceData,
  setShowCaptions as setShowCaptionsLocalStorage,
} from '../utilities/localStorage/index.js';
import {
  NEW_TRANSCRIPT_EXPERIENCE_OWNER,
  NEW_TRANSCRIPT_EXPERIENCE_VIEWER,
  SHOW_FIRST_CAPTION_EXPERIENCE,
} from '../../../shared/constants/triggers.js';
import { SHARE_PAGE } from '../../../shared/constants/uiLocations';
import {
  THUMB_DEFAULT,
  THUMB_GIF,
  THUMB_GIF_PLAY,
  WAVEFORM_FAILURE,
  WAVEFORM_IN_PROGRESS,
  WAVEFORM_SUCCESS,
} from '../../../shared/constants/video';
import { ADD_VIDEO_TO_TEAM_CARD_KEY } from '../../../shared/constants/workspaceOnboarding';
import { TEAM_WORKSPACE } from '../../../shared/constants/workspaces';

export function initVideoCreator(videoModel) {
  return (dispatch, getState) => {
    dispatch(updateShareVideo(videoModel));
    dispatch(initVideoPlayerVideoCreator(videoModel));
    videoUtils.setDocumentTitleForVideo(getState());
  };
}

async function preloadThumbnail(videoModel, dispatch) {
  const posterUrl = videoUtils.getPosterUrl(videoModel);

  if (posterUrl) {
    await preloadImage(posterUrl);
    dispatch(updateVideoPlayerPosterUrl(posterUrl));
  }
}

function updateVideoThumbnails(data) {
  return (dispatch, getState) => {
    const { video } = getState().share_video;
    const { thumbnails, use_gif, defaultThumbnails } = data;
    const patchedVideo = {
      ...video,
      use_gif,
      thumbnails: {
        ...video.thumbnails,
        [THUMB_GIF]: thumbnails.defaultGif,
        [THUMB_DEFAULT]: thumbnails.default,
        [THUMB_GIF_PLAY]: thumbnails.defaultGifPlay,
      },
      defaultThumbnails,
    };

    dispatch(updateShareVideo(patchedVideo));
    dispatch(updateVideoPlayerVideo(patchedVideo));
    preloadThumbnail(patchedVideo, dispatch);
  };
}

export function subscribeToGif(videoId) {
  return dispatch => {
    const client = getGraphQLClient();
    const gifsObservable = client
      .subscribe({
        variables: { videoId },
        query: onGifGeneratedForVideo,
      })
      .subscribe(() => {
        dispatch(fetchThumbnails(videoId));
        gifsObservable.unsubscribe();
      });
  };
}

function fetchThumbnails(videoId) {
  return dispatch => {
    const client = getGraphQLClient();

    client
      .query({
        variables: { videoId },
        query: GetVideoThumbnails,
      })
      .then(({ data }) => {
        if (data.video.__typename !== 'RegularUserVideo') {
          return;
        }

        dispatch(updateVideoThumbnails(data.video));
      });
  };
}

export function fetchVideoCreator(videoId) {
  return async (dispatch, getState) => {
    try {
      const resp = await fetch(`/v1/videos/${videoId}`, {
        credentials: 'include',
      });

      if (resp.status >= 400) {
        if (resp.status === STATUS_NOT_FOUND) {
          window.location.href = NOT_FOUND;
        } else {
          window.location.href = SERVER_ERROR_PAGE;
        }

        return;
      }

      const video = await resp.json();

      dispatch(updateShareVideo(video));
      dispatch(updateVideoPlayerVideo(video));
      preloadThumbnail(video, dispatch);
      videoUtils.setDocumentTitleForVideo(getState());
    } catch (err) {
      sentry.logException(err, 'Error fetching from /v1/videos');

      window.location.href = SERVER_ERROR_PAGE;
    }
  };
}

export function updateVideoNameCreator(newName = null) {
  return async (dispatch, getState) => {
    const { share_video } = getState();
    const { video } = share_video;

    if (!newName || !video || video.name === newName) {
      return;
    }

    dispatch(updateShareVideo({ ...video, name: newName }));
    videoUtils.setDocumentTitleForVideo(getState());

    const gqlClient = getGraphQLClient();

    try {
      const { data } = await gqlClient.mutate({
        mutation: UpdateVideoName,
        variables: {
          id: video.id,
          name: newName,
        },
      });

      if (data?.result?.__typename === 'UpdateVideoNamePayload') {
        analytics.track(SHARE_VIDEO_NAME_UPDATED, {
          id: video.id,
          oldLength: video.name.length,
          newLength: newName.length,
          source: 'video-share-page',
        });
      } else {
        throw new Error(
          `Oops! Error updating video name - GenericError: error - ${data.error}, message - ${data.message}`
        );
      }
    } catch (err) {
      sentry.logException(err, 'error in share page update video name creator');
      dispatch(showErrorBar(ERROR, err.toString()));
      dispatch(updateShareVideo(video));
    }
  };
}

export function finishUploadCreator(videoId) {
  return dispatch => {
    dispatch(fetchVideoCreator(videoId));
    dispatch(updateVideoIframeVisibility());
    dispatch(fetchShareVideoDownloadUrlCreator());
  };
}

export function updateVideoCtaCreator() {
  return (dispatch, getState) => {
    const { cta_enabled, cta_text, cta_url, cta_mods, video } =
      getState().share_video;

    let ctaText = cta_text;

    if (cta_enabled && !cta_text) {
      ctaText = 'Click Here!';
    }

    const newCta = {
      enabled: cta_enabled,
      text: ctaText,
      url: cta_url,
      mods: cta_mods,
    };

    return fetch(`/v1/videos/${video.id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body: JSON.stringify({ cta: newCta }),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error updating video cta - HTTP response: ${resp.status}`
          );
        }
      })
      .catch(err => {
        sentry.logException(
          err,
          'error in share page update video cta creator'
        );
        dispatch(showErrorBar(ERROR, err.toString()));
        dispatch(updateShareVideo(video));
      });
  };
}

export function moveVideoCreator(
  newFolderId,
  newFolderName,
  archived = false,
  destinationWorkspace
) {
  return (dispatch, getState) => {
    dispatch(updateShowMoveModal(false));

    const { share_video, workspace, videos_dashboard } = getState();
    const { workspace: fromWorkspace } = videos_dashboard;

    const id = share_video.video.id;

    const { video_folders, video } = share_video;
    const fromFolderId = video_folders.slice(-1)[0].id;

    let body;

    if (archived != null) {
      body = JSON.stringify({
        folder_id: newFolderId,
        archived,
      });
    } else {
      body = JSON.stringify({ folder_id: newFolderId });
    }

    return fetch(`/v1/videos/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error moving video: ${video.name} ` +
              `- HTTP response: ${resp.status}`
          );
        }

        return resp.json();
      })
      .then(newVideo => {
        analytics.track(VIDEO_MOVED, {
          archiving: archived,
          current_folder_id: fromFolderId,
          new_folder_id: newFolderId,
          video_name: newVideo.name,
          from_library: fromWorkspace,
          to_library: destinationWorkspace,
          id,
          source: 'video-share-page',
        });

        dispatch(
          showConfirmationModal(
            `${video.name} successfully moved to ${newFolderName}`
          )
        );
        dispatch(videoMoveSuccess(newVideo.folders));
        dispatch(updateShareVideo(newVideo));

        if (destinationWorkspace === TEAM_WORKSPACE) {
          markEligibleElementAsCompleteForSelectedWorkspace(
            dispatch,
            workspace.workspaces,
            ADD_VIDEO_TO_TEAM_CARD_KEY
          );
        }
      })
      .catch(err => {
        sentry.logException(err, 'error in share page move video creator');
        dispatch(showErrorBar(ERROR, err.toString()));
      });
  };
}

export function createFolderCreator(parentFolderId = null, folderName) {
  if (!folderName) {
    return;
  }

  return dispatch => {
    const localId = new Date().getTime();
    const body = JSON.stringify({
      parent_folder_id: parentFolderId,
      name: folderName || undefined,
      local_id: localId,
    });

    dispatch(createNewFolderLoad(parentFolderId, folderName, localId));

    return fetch('/v1/folders', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body,
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error creating folder - HTTP response: ${resp.status}`
          );
        }

        return resp.json();
      })
      .then(folder => {
        dispatch(createNewFolderSuccess(folder));
      })
      .catch(err => {
        dispatch(createNewFolderFailed());
        sentry.logException(err, 'error in create folder creator');
        dispatch(showErrorBar(ERROR, err.toString()));
      });
  };
}

export function updateGifSettings(videoId, useGif, useGifDefault) {
  return dispatch => {
    const client = getGraphQLClient();

    dispatch(endThumbnailFlow());
    dispatch(showConfirmationModal('Your preferences have been saved'));
    client
      .mutate({
        variables: { videoId, useGif, useGifDefault },
        mutation: UpdateGifSettings,
      })
      .then(({ data }) => {
        const { updateVideoGifSettings = null } = data;

        if (!updateVideoGifSettings) {
          return;
        }

        dispatch(updateVideoThumbnails(updateVideoGifSettings));
      })
      .catch(err => {
        throw new Error(`Oops! Error extracting gif - GraphQL: ${err}`);
      });
  };
}

export function subscribeToTranscriptStatus(password = null) {
  return (dispatch, getState) => {
    const { video, transcriptStatus } = getState().video_player;

    try {
      if (
        !video.active_video_transcript_id ||
        transcriptStatus === TRANSCRIPTION.SUCCESS ||
        transcriptStatus === TRANSCRIPTION.FAILED ||
        transcriptStatus === TRANSCRIPTION.NO_AUDIO ||
        transcriptStatus === TRANSCRIPTION.SHORT_DURATION
      ) {
        // Transcription status is success/failed/no_audio/short_duration. Not subscribing
        return;
      }

      const client = getGraphQLClient();
      const subscription = client
        .subscribe({
          variables: { videoId: video.id },
          query: gql`
            subscription FetchTranscriptStatus($videoId: ID!) {
              transcriptStatus(videoId: $videoId) {
                status
              }
            }
          `,
        })
        .subscribe(body => {
          const {
            data: {
              transcriptStatus: { status },
            },
          } = body;

          if (TRANSCRIPTION.TRANSCRIPTION_STATUSES.includes(status)) {
            dispatch(updateTranscriptStatus(status));
            dispatch(updateTranscriptStatusOnVideoPlayer(status));
          }

          if (
            status === TRANSCRIPTION.SUCCESS ||
            status === TRANSCRIPTION.FAILED ||
            !TRANSCRIPTION.TRANSCRIPTION_STATUSES.includes(status)
          ) {
            dispatch(fetchTranscriptPhrases(password));
            subscription.unsubscribe();
          }
        });

      // Just in case the transcript completed between the initial fetch and the subscribe
      // We fetch here to avoid a race condition
      dispatch(fetchTranscriptPhrases(password));

      return subscription;
    } catch (err) {
      loggerx.error(
        err,
        {
          message: `Error subscribing to transcript status for video id ${video.id}: ${err}`,
          videoId: video.id,
        },
        { team: 'create' }
      );

      throw new Error(
        `Error subscribing to transcript status for video id ${video.id}: ${err}`
      );
    }
  };
}

export function fetchWaveformGenerationDetails(password = null) {
  return async (dispatch, getState) => {
    const { video } = getState().video_player;
    const client = getGraphQLClient();
    const { data } = await client.query({
      query: FetchVideoWaveform,
      variables: {
        id: video.id,
        password,
      },
      fetchPolicy: 'no-cache',
    });

    if (data?.fetchVideoWaveform) {
      const {
        fetchVideoWaveform: { status, source_url },
      } = data;

      dispatch(updateWaveformGenerationDetails(status, source_url));
    }
  };
}

export function subscribeToWaveformGenerationStatus(password = null) {
  return (dispatch, getState) => {
    const { video } = getState().video_player;

    if (
      [WAVEFORM_SUCCESS, WAVEFORM_FAILURE].includes(video.waveformGeneration)
    ) {
      loggerx.info(
        `Waveform generation status is ${video.waveform_generation}. Not subscribing`
      );

      return;
    }

    try {
      const client = getGraphQLClient();
      const subscription = client
        .subscribe({
          variables: { id: video.id },
          query: gql`
            subscription WaveformGenerationStatus($id: ID!) {
              waveformGeneration(id: $id) {
                status
              }
            }
          `,
        })
        .subscribe(body => {
          const {
            data: {
              waveformGeneration: { status },
            },
          } = body;

          loggerx.info(`Waveform status updated: ${status}`);

          if (
            [WAVEFORM_SUCCESS, WAVEFORM_FAILURE, WAVEFORM_IN_PROGRESS].includes(
              status
            )
          ) {
            dispatch(updateWaveformGenerationStatus(status));
          }

          if ([WAVEFORM_SUCCESS, WAVEFORM_FAILURE].includes(status)) {
            dispatch(fetchWaveformGenerationDetails(password));
            subscription.unsubscribe();
          }
        });

      // Just in case the waveform generation completed between the initial fetch and the subscribe
      // We fetch here to avoid a race condition
      dispatch(fetchWaveformGenerationDetails(password));

      return subscription;
    } catch (err) {
      const message = `Error subscribing to waveform generation status for video ${video.id}: ${err}`;

      loggerx.error(
        err,
        {
          message,
          videoId: video.id,
        },
        { team: 'create' }
      );

      throw new Error(message);
    }
  };
}

let fetchTranscriptInProgress = false;

export function fetchTranscriptPhrases(password = null) {
  return async (dispatch, getState) => {
    if (fetchTranscriptInProgress) {
      // another fetch is already in progress, abort this one.
      return;
    }

    const { video } = getState().video_player;

    try {
      fetchTranscriptInProgress = true;

      if (!video.active_video_transcript_id) {
        return;
      }

      const client = getGraphQLClient();
      const {
        data: { fetchVideoTranscript },
      } = await client.query({
        query: FetchVideoTranscript,
        variables: {
          videoId: video.id,
          password,
        },
        fetchPolicy: 'no-cache',
      });

      if (fetchVideoTranscript['__typename'] === 'GenericError') {
        throw new Error(`GraphQL error: ${fetchVideoTranscript.message}`);
      }

      const {
        captions_url: captionsUrl,
        captions_source_url: captionsSourceUrl,
        filler_words: fillerWords,
        filler_word_removal: fillerWordRemoval,
      } = fetchVideoTranscript;

      ['__typename', 'captions_url', 'captions_source_url'].forEach(
        key => delete fetchVideoTranscript[key]
      );

      dispatch(updateTranscriptDetails(fetchVideoTranscript));
      dispatch(updateCaptionsDetails({ captionsUrl, captionsSourceUrl }));
      dispatch(updateFillerWords(fillerWords, fillerWordRemoval));

      const sourceUrl = fetchVideoTranscript?.source_url || null;
      const status = fetchVideoTranscript?.transcription_status || null;

      dispatch(
        updateTranscriptStatusOnVideoPlayer(
          fetchVideoTranscript['transcription_status']
        )
      );

      if (status === TRANSCRIPTION.SUCCESS && sourceUrl) {
        const resp = await fetch(sourceUrl);

        if (!resp.ok) {
          throw new Error('Failed to fetch transcript json');
        }

        const phrasesObj = await resp.json();

        dispatch(updateTranscriptPhrases(phrasesObj));
      }
    } catch (err) {
      loggerx.error(
        err,
        {
          message: `Error fetching transcript for video id ${video.id}: ${err}`,
          videoId: video.id,
        },
        { team: 'create' }
      );

      throw new Error(
        `Error fetching transcript for video id ${video.id}: ${err}`
      );
    } finally {
      fetchTranscriptInProgress = false;
    }
  };
}

export function updateTranscriptByFillerWordRemoval(fillerWordRemoval) {
  return async (dispatch, getState) => {
    if (!fillerWordRemoval) {
      return;
    }

    const { video } = getState().video_player;
    const {
      transcript_details: { version: prevVersion },
    } = getState().share_video;

    const gqlClient = getGraphQLClient();

    try {
      const {
        data: { updateFillerWordRemoval },
      } = await gqlClient.mutate({
        mutation: UpdateFillerWordRemoval,
        variables: {
          videoId: video.id,
          fillerWordRemoval,
        },
      });

      const { version } = updateFillerWordRemoval;

      if (version && prevVersion + 1 === version) {
        dispatch(fetchTranscriptPhrases());
        dispatch(updateTrimmingFillerWords(false));
        dispatch(requestVideoPlayerPause());

        analytics.track(UPDATE_TRANSCRIPT_BY_FILLER_WORD_REMOVAL, {
          videoId: video.id,
          fillerWordRemoval,
        });
      }
    } catch (err) {
      loggerx.error(
        err,
        {
          message: `Error updating transcript for video id ${video.id} after filler word removal: ${err}`,
          videoId: video.id,
        },
        { team: 'create' }
      );

      throw new Error(
        `Error updating transcript for video id ${video.id} after filler word removal: ${err}`
      );
    }
  };
}

export function updateVideoSettings({
  newSettings = {},
  password,
  refetchSourceUrl,
}) {
  return (dispatch, getState) => {
    const { video } = getState().share_video;

    if (video == null) {
      return;
    }

    return fetch(`/v1/videos/${video.id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body: JSON.stringify({ ...newSettings, password }),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error updating video - HTTP response: ${resp.status}`
          );
        }

        return resp.json();
      })
      .then(patchedVideo => {
        dispatch(updateShareVideo(patchedVideo));
        dispatch(updateVideoPlayerVideo(patchedVideo));
        preloadThumbnail(patchedVideo, dispatch);

        window.top.postMessage(
          {
            type: SHARE_VIDEO_FLIP_CAMERA,
            [FLIPPED_CAMERA]: newSettings[FLIPPED_CAMERA],
          },
          '*'
        );

        if (refetchSourceUrl) {
          const timeBeforeSwap = selectPlayerTime(getState());

          dispatch(updateVideoPlayerSourceCreator());
          dispatch(requestVideoPlayerCurrentTime(timeBeforeSwap));
        }
      })
      .catch(err => {
        sentry.logException(err, 'error in updateVideoSettings creator');
        dispatch(showErrorBar(ERROR, err.toString()));
      });
  };
}

export function fetchShareVideoDownloadUrlCreator() {
  return (dispatch, getState) => {
    try {
      const {
        password,
        video,
        current_user_is_owner: currentUserIsOwner,
      } = getState().share_video;

      const { download_enabled } = video;

      if (!(download_enabled || currentUserIsOwner)) {
        return;
      }

      if (!currentUserIsOwner && video?.is_protected && password == null) {
        return videoUtils.cancelPostSessionTranscodedURL();
      }

      videoUtils.postSessionTranscodedURL(video?.id, password, mp4Url => {
        dispatch(updateShareVideoDownloadUrl(mp4Url));
      });
    } catch (err) {
      sentry.logException(err, 'error fetching share video download url');
    }
  };
}

export function archiveVideoCreator(videoId) {
  return async dispatch => {
    try {
      const resp = await fetch(`/v1/videos/${videoId}`, {
        credentials: 'include',
        headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
        method: 'PATCH',
        body: JSON.stringify({ archived: true }),
      });

      if (resp.status >= 400) {
        throw new Error(
          `Oops! Error archiving video - HTTP response: ${resp.status}`
        );
      }

      window.location.href = `${LOOM_URI}${MY_VIDEOS_PAGE}`;
    } catch (err) {
      sentry.logException(err, 'error archiving share video');
      dispatch(showErrorBar(ERROR, err.toString()));
    }
  };
}

export function deleteVideoCreator(videoId) {
  return async dispatch => {
    try {
      const client = getGraphQLClient();

      const { data } = await client.mutate({
        mutation: DestroyVideo,
        variables: {
          videoId,
          video_properties: { ui_location: SHARE_PAGE },
        },
      });

      if (data?.destroyVideo?.success) {
        window.location.href = `${LOOM_URI}${MY_VIDEOS_PAGE}`;
      } else {
        const msg = data?.destroyVideo?.message;

        if (msg) {
          throw new Error(msg);
        }
      }
    } catch (err) {
      sentry.logException(err, `Error deleting share video ${videoId}`);
      dispatch(showErrorBar(ERROR, 'Error deleting share video'));
    }
  };
}

export function uploadVideoThumbnail(videoId, imgDataUrl, croppedDims) {
  return dispatch => {
    dispatch(toggleThumbnailUploading());

    const MAX_FILE_SIZE = 1000000 * 4;

    const dataURLtoBlob = dataurl => {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);

      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new Blob([u8arr], { type: mime });
    };

    const file = dataURLtoBlob(imgDataUrl);

    if (file.size > MAX_FILE_SIZE) {
      dispatch(
        showErrorBar(
          ERROR,
          'Selected file too big. Max 4 MB. Please try again.'
        )
      );
      dispatch(toggleThumbnailUploading());

      return;
    }

    const formData = new FormData();

    formData.append('thumbnail', file);
    formData.append('dims', JSON.stringify(croppedDims));

    return fetch(`/v1/videos/${videoId}`, {
      method: 'PATCH',
      credentials: 'include',
      body: formData,
    })
      .then(response => {
        if (response.status >= 400) {
          throw new Error(`got a bad response status: ${response.status}`);
        }

        dispatch(toggleThumbnailUploading());
        dispatch(showConfirmationModal('New thumbnail saved'));
        dispatch(finishThumbnailSave());

        return response.json();
      })
      .then(video => {
        dispatch(updateShareVideo(video));
        dispatch(updateVideoPlayerCreator(video));
      })
      .catch(error => {
        dispatch(
          showErrorBar(ERROR, 'Oops! Failed to update video thumbnail.')
        );
        sentry.logException(error, 'Error in PATCH to /v1/videos/thumbnail');
      });
  };
}

export function removeVideoThumbnail(videoId) {
  return dispatch => {
    return fetch(`/v1/videos/${videoId}`, {
      method: 'PATCH',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      credentials: 'include',
      body: JSON.stringify({ thumbnails: null }),
    })
      .then(response => {
        if (response.status >= 400) {
          throw new Error(`got a bad response status: ${response.status}`);
        }

        dispatch(showConfirmationModal('Thumbnail deleted'));

        return response.json();
      })
      .then(video => {
        dispatch(updateShareVideo(video));
        dispatch(updateVideoPlayerCreator(video));
      })
      .catch(error => {
        dispatch(
          showErrorBar(ERROR, 'Oops! Failed to remove video thumbnail.')
        );
        sentry.logException(error, 'Error in PATCH to /v1/videos/thumbnail');
      });
  };
}

export function createShareVideoAlias(videoId, email) {
  return async (dispatch, getState) => {
    const { shareVideoAliases } = getState().share_video;

    const client = getGraphQLClient();

    try {
      const {
        data: { createShareVideoAlias: newAlias },
      } = await client.mutate({
        mutation: CreateShareVideoAlias,
        variables: { videoId, email },
      });

      if (!newAlias) {
        throw new Error(
          'Something went wrong while creating aliased video link'
        );
      }

      dispatch(updateShareVideoAliases([...shareVideoAliases, newAlias]));
    } catch (err) {
      dispatch(showErrorBar(ERROR, 'Oops! Unable to create video link alias.'));

      sentry.logException(err, 'error creating video aliased link');
    }
  };
}

export function getShareVideoAlias(videoId) {
  return async dispatch => {
    const client = getGraphQLClient();

    try {
      const {
        data: { findBulkShareVideoAlias },
      } = await client.query({
        query: GetBulkShareVideoAlias,
        variables: { videoId },
        fetchPolicy: 'no-cache',
      });

      if (!findBulkShareVideoAlias) {
        throw new Error(
          'Something went wrong while creating aliased video link'
        );
      }

      dispatch(updateShareVideoAliases(findBulkShareVideoAlias));
    } catch (err) {
      dispatch(
        showErrorBar(ERROR, 'Oops! Unable to fetch video link aliases.')
      );

      sentry.logException(err, 'error fetch video aliased links');
    }
  };
}

export function deleteShareVideoAlias(shareVideoAliasId) {
  return async (dispatch, getState) => {
    const { shareVideoAliases } = getState().share_video;

    const client = getGraphQLClient();

    try {
      const {
        data: { deleteShareVideoAlias: success },
      } = await client.mutate({
        mutation: DeleteShareVideoAlias,
        variables: { shareVideoAliasId },
      });

      if (!success) {
        throw new Error(
          'Something went wrong while deleting aliased video link'
        );
      }

      const newAliases = shareVideoAliases.filter(
        ({ id }) => id !== shareVideoAliasId
      );

      dispatch(updateShareVideoAliases(newAliases));
    } catch (err) {
      dispatch(showErrorBar(ERROR, 'Oops! Unable to delete video link alias.'));

      sentry.logException(err, 'error deleting video aliased link');
    }
  };
}

export function toggleVideoFavoriteCreator(video) {
  return async dispatch => {
    try {
      const client = getGraphQLClient();

      await client.mutate({
        mutation: video.isUserFavorite ? DeleteFavorite : AddFavorite,
        variables: { entities: [{ id: video.id, type: 'video' }] },
      });

      analytics.track(VIDEO_FAVORITED, {
        favorite: !video.isUserFavorite, // the new value is the opposite of the old one
        workspace: video.workspace,
        id: video.id,
      });
    } catch (err) {
      sentry.logException(err, 'Error toggle folder favorite');
      dispatch(showErrorBar(err, 'Error adding favorite'));
    }
  };
}

export function handleToggleTranscriptCreator() {
  return async (dispatch, getState) => {
    const {
      full_screen: fullScreen,
      transcriptVisible,
      video: { id: videoId, current_user_is_owner: currentUserIsOwner },
      shouldReprocessTranscriptForFillerWords,
    } = getState().video_player;

    const { in_trim_mode: inTrimMode } = getState().trimming;

    const { is_logged_in: isLoggedIn } = getState().user;

    const { userHasOpenedTranscript } = getState().share_video;

    if (inTrimMode) {
      return;
    }

    const shouldTranscriptBeVisible = !transcriptVisible;

    const isInFillerWordRemovalFlag = await getFlagValueForUser(
      STORE_FILLER_WORD_RANGES
    );

    const shouldReprocessForFillerWords =
      shouldReprocessTranscriptForFillerWords && isInFillerWordRemovalFlag;

    if (shouldTranscriptBeVisible && !userHasOpenedTranscript) {
      if (isLoggedIn) {
        await dispatch(
          completeTriggerCreator(NEW_TRANSCRIPT_EXPERIENCE_VIEWER)
        );
        await dispatch(completeTriggerCreator(NEW_TRANSCRIPT_EXPERIENCE_OWNER));
      } else {
        setNewTranscriptExperienceData({
          complete: true,
          show: false,
        });
      }
    }

    if (!userHasOpenedTranscript) {
      dispatch(updateTheaterMode(false));
      dispatch(requestVideoPlayerLeaveFullScreen());
      dispatch(toggleTranscriptVisible(true));
      dispatch(updateUserHasOpenedTranscript());
    } else if (fullScreen) {
      dispatch(requestVideoPlayerLeaveFullScreen());
      dispatch(toggleTranscriptVisible(true));
    } else {
      dispatch(toggleTranscriptVisible(shouldTranscriptBeVisible));
    }

    if (shouldTranscriptBeVisible) {
      if (shouldReprocessForFillerWords) {
        dispatch(resetTranscriptState());
        dispatch(updateTranscriptByFillerWordRemoval(TRANSCRIPTION.NONE));
      }

      analytics.track(TRANSCRIPTION_ACCESSED, {
        currentUserIsOwner,
        video_id: videoId,
        transcript_visible: shouldTranscriptBeVisible,
      });
    } else {
      analytics.track(TRANSCRIPTION_CLOSE_OUT, {
        currentUserIsOwner,
        video_id: videoId,
        transcript_visible: shouldTranscriptBeVisible,
      });
    }
  };
}

export function handleUpdateCaptionsVisible(isVisible) {
  return async (dispatch, getState) => {
    dispatch(toggleCaptionsVisible(isVisible));
    setShowCaptionsLocalStorage(isVisible);

    const {
      video: { id: videoId, current_user_is_owner: currentUserIsOwner },
    } = getState().video_player;

    const { is_logged_in: isLoggedIn } = getState().user;

    if (isLoggedIn) {
      await dispatch(completeTriggerCreator(SHOW_FIRST_CAPTION_EXPERIENCE));
    } else {
      setNewCcExperienceData({
        complete: true,
        show: false,
      });
    }

    if (isVisible) {
      analytics.track(TOGGLE_CAPTIONS_ON, {
        currentUserIsOwner,
        video_id: videoId,
        captions_visible: isVisible,
      });
    } else {
      analytics.track(TOGGLE_CAPTIONS_OFF, {
        currentUserIsOwner,
        video_id: videoId,
        captions_visible: isVisible,
      });
    }
  };
}

export function updateLocalVideoCreator(video, patch) {
  return updateShareVideo({
    ...video,
    ...patch,
  });
}

export function fetchAnonCreatorMode(videoId) {
  return async (dispatch, getState) => {
    const state = getState();

    if (state.user?.is_logged_in) {
      return;
    }

    const flagValue = await dispatch(
      getFeatureFlagCreator(EXP_PERSISTENT_RECORD_BUTTON_SIGNED_OUT)
    );

    if (flagValue !== 'variant') {
      return;
    }

    if (isDev && getParam('anon-creator')) {
      dispatch(setAnonCreatorMode(true));

      return;
    }

    const client = getGraphQLClient();

    const { data } = await client.query({
      query: HasAnonymousCreatorPrivileges,
      variables: {
        videoId,
      },
    });

    if (data.result.hasPrivileges) {
      dispatch(setAnonCreatorMode(true));
    }
  };
}
